import './voice.css';
import type { VoiceStatus } from '../net/VoiceChat';
import { t, onLang } from './i18n';

/**
 * Voice-chat controls: a mic join/leave toggle, a mute button, and a status dot.
 * The panel renders only during online play (toggled from App via show/hide).
 */
export interface VoicePanelCallbacks {
  onToggle(): void;
  onMute(muted: boolean): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class VoicePanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly joinBtn = el('button', 'voice-join');
  private readonly muteBtn = el('button', 'voice-mute');
  private readonly dot = el('span', 'voice-dot');
  private readonly statusLabel = el('span');
  private status: VoiceStatus = 'off';
  private muted = false;

  constructor(root: HTMLElement, cb: VoicePanelCallbacks) {
    this.panel.id = 'voice-panel';

    this.joinBtn.onclick = () => cb.onToggle();
    this.muteBtn.onclick = () => cb.onMute(!this.muted);

    const status = el('span', 'voice-status');
    status.append(this.dot, this.statusLabel);
    this.panel.append(this.joinBtn, this.muteBtn, status);
    root.appendChild(this.panel);

    onLang(() => this.render()); // re-label on language change
    this.hide();
    this.render();
  }

  show(): void {
    this.panel.style.display = 'flex';
  }

  hide(): void {
    this.panel.style.display = 'none';
  }

  /** Reflect the VoiceChat lifecycle (called on every state change). */
  setStatus(status: VoiceStatus, muted: boolean): void {
    this.status = status;
    this.muted = muted;
    this.render();
  }

  private render(): void {
    const on = this.status !== 'off' && this.status !== 'failed';
    this.joinBtn.textContent = t(on ? 'voice.leave' : 'voice.join');
    this.joinBtn.classList.toggle('on', on);

    this.muteBtn.textContent = t(this.muted ? 'voice.unmute' : 'voice.mute');
    this.muteBtn.classList.toggle('muted', this.muted);
    this.muteBtn.disabled = !on;

    this.dot.className = `voice-dot ${this.status}`;
    this.statusLabel.textContent = t(`voice.status.${this.status}`);
  }
}
